import { motion } from "framer-motion";
import { Clock, MapPin, ExternalLink } from "lucide-react";

/**
 * Fall 2026 Season · Aug – Nov
 * Marked session days:
 *  · Aug 2026 → 1st & 3rd SATURDAY  (Aug 1, Aug 15)
 *  · Sep–Nov 2026 → 1st & 3rd SUNDAY (Sep 6, Sep 20 · Oct 4, Oct 18 · Nov 1, Nov 15)
 */

const months = [
  { name: "August",    year: 2026, first: 6 /* Sat */,  days: 31, markDays: [8, 22]  }, // 2nd, 4th Sat
  { name: "September", year: 2026, first: 2 /* Tue */,  days: 30, markDays: [6, 20]  }, // 1st, 3rd Sun
  { name: "October",   year: 2026, first: 4 /* Thu */,  days: 31, markDays: [4, 18]  }, // 1st, 3rd Sun
  { name: "November",  year: 2026, first: 0 /* Sun */,  days: 30, markDays: [1, 15]  }, // 1st, 3rd Sun
];

const dayHeaders = ["S", "M", "T", "W", "T", "F", "S"];

const times = [
  { name: "Sprouts",    band: "Ages 5 – 8",   time: "2:00 – 3:00 PM" },
  { name: "Explorers",  band: "Ages 9 – 12",  time: "3:30 – 4:30 PM" },
  { name: "Navigators", band: "Ages 13 – 18", time: "5:00 – 6:00 PM" },
];

const VENUE = "2602 N Loop 1604 W #201, San Antonio, TX 78248";
const VENUE_MAP = `https://maps.google.com/?q=${encodeURIComponent(VENUE)}`;

function MonthCalendar({ month, index }) {
  const cells = [];
  for (let i = 0; i < month.first; i++) cells.push(null);
  for (let d = 1; d <= month.days; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: index * 0.08 }}
      className="border hairline p-5 md:p-6 paper-bg"
      data-testid={`schedule-month-${month.name.toLowerCase()}`}
    >
      <div className="flex items-baseline justify-between mb-4">
        <h3 className="font-display text-2xl md:text-3xl tracking-tight text-[color:var(--ink)]">
          {month.name}
        </h3>
        <span className="font-mono-editorial text-[10px] tracking-[0.24em] text-[color:var(--muted)]">
          {month.year}
        </span>
      </div>

      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayHeaders.map((d, i) => (
          <div
            key={i}
            className="text-[10px] font-mono-editorial tracking-[0.14em] text-[color:var(--muted)]/70 text-center"
          >
            {d}
          </div>
        ))}
      </div>

      {/* Dates */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={i} className="aspect-square" />;
          const marked = month.markDays.includes(d);
          return (
            <div
              key={i}
              className="aspect-square flex items-center justify-center relative"
              data-testid={marked ? `session-date-${month.name.toLowerCase()}-${d}` : undefined}
            >
              {marked ? (
                <span
                  className="relative w-full h-full rounded-full flex items-center justify-center font-serif-editorial text-lg md:text-xl"
                  style={{ background: "var(--ink)", color: "var(--inv)" }}
                >
                  {d}
                  <span
                    className="absolute -top-1 -right-1 w-2 h-2 rounded-full"
                    style={{ background: "var(--brand)" }}
                    aria-hidden
                  />
                </span>
              ) : (
                <span className="font-serif-editorial text-sm md:text-base text-[color:var(--ink)]/60">
                  {d}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </motion.div>
  );
}

export default function Schedule() {
  return (
    <section
      id="schedule"
      className="py-24 md:py-40 relative overflow-hidden"
      style={{ background: "var(--bg-2)" }}
      data-testid="schedule-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 relative">
        {/* Header */}
        <div className="mb-14 md:mb-20">
          <h2
            className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.92] tracking-tight text-[color:var(--ink)]"
            data-testid="schedule-heading"
          >
            Foundational year <span className="italic font-serif-editorial" style={{ color: "var(--brand)" }}>(Fall 2026)</span>
          </h2>
        </div>

        {/* Big calendar row */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5"
          data-testid="schedule-calendar-grid"
        >
          {months.map((m, i) => (
            <MonthCalendar key={m.name} month={m} index={i} />
          ))}
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-[11px] font-mono-editorial tracking-[0.2em] text-[color:var(--muted)]">
          <span className="flex items-center gap-2">
            <span className="inline-block w-4 h-4 rounded-full relative" style={{ background: "var(--ink)" }}>
              <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full" style={{ background: "var(--brand)" }} />
            </span>
            SESSION DAY
          </span>
          <span>· AUG · SAT 8 & 22</span>
          <span>· SEP–NOV · 1ST & 3RD SUNDAY</span>
        </div>

        {/* Session times + Venue */}
        <div className="mt-16 md:mt-20 grid grid-cols-12 gap-4 md:gap-6">
          {/* Times */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="col-span-12 lg:col-span-7 border hairline p-8 md:p-10 paper-bg"
            data-testid="schedule-times"
          >
            <div className="flex items-center gap-3 mb-6">
              <Clock size={18} strokeWidth={1.75} />
              <span className="font-mono-editorial text-[11px] tracking-[0.28em] text-[color:var(--muted)]">
                SESSION TIMES · EACH SESSION DAY
              </span>
            </div>

            <div className="divide-y hairline divide-y-[1px]">
              {times.map((t, i) => (
                <div
                  key={t.name}
                  className="py-5 md:py-6 grid grid-cols-12 gap-4 items-baseline"
                  data-testid={`schedule-time-${t.name.toLowerCase()}`}
                >
                  <div className="col-span-12 md:col-span-2">
                    <span className="font-mono-editorial text-[10px] tracking-[0.28em] text-[color:var(--muted)]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h4 className="col-span-8 md:col-span-4 font-display text-3xl md:text-4xl leading-none tracking-tight">
                    {t.name}
                  </h4>
                  <div className="col-span-4 md:col-span-2 font-mono-editorial text-[10px] tracking-[0.24em] text-[color:var(--muted)] text-right md:text-left">
                    {t.band}
                  </div>
                  <div className="col-span-12 md:col-span-4 md:text-right font-serif-editorial italic text-2xl md:text-3xl text-[color:var(--ink)]">
                    {t.time}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Venue */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="col-span-12 lg:col-span-5 border p-8 md:p-10 relative overflow-hidden flex flex-col justify-between"
            style={{ background: "var(--ink)", color: "#fff", borderColor: "var(--ink)" }}
            data-testid="schedule-venue"
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <MapPin size={18} strokeWidth={1.75} />
                <span className="font-mono-editorial text-[11px] tracking-[0.28em] text-white/60">
                  VENUE · IN-PERSON
                </span>
              </div>
              <p className="font-display text-3xl md:text-4xl leading-[1.05] tracking-tight text-white max-w-[22ch]">
                2602 N Loop 1604 W&nbsp;#201
                <br />
                <span className="text-white/80">San Antonio, TX 78248</span>
              </p>
              <p className="mt-5 text-white/60 text-sm leading-relaxed">
                All sessions are held in-person at our San Antonio studio. Doors open 15 minutes before class.
              </p>
            </div>

            <a
              href={VENUE_MAP}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center justify-between h-12 px-5 rounded-full transition-all duration-500 hover:-translate-y-0.5"
              style={{ background: "var(--brand)", color: "#fff" }}
              data-testid="schedule-venue-map"
            >
              <span className="text-sm tracking-wide flex items-center gap-2">
                Open in Google Maps <ExternalLink size={14} />
              </span>
              <span>→</span>
            </a>

            {/* subtle brand watermark */}
            <span
              className="pointer-events-none absolute -right-6 -bottom-6 font-display text-[9rem] leading-none tracking-tighter select-none"
              style={{ color: "rgba(255,255,255,0.04)" }}
              aria-hidden
            >
              शेमुषी
            </span>
          </motion.div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 md:mt-16">
          <p className="font-mono-editorial text-[11px] tracking-[0.22em] text-[color:var(--ink)]/70">
            · Small in-person batches ·
          </p>
        </div>
      </div>
    </section>
  );
}
